import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICE_GROUPS } from "@/data/serviceOptions";
import { toast } from "@/components/ui/sonner";
import { AppointmentPicker } from "@/components/AppointmentPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = { name: "", phone: "", email: "", service: "", stage: "", message: "", website: "" };
const fieldClass = "mt-3 h-12 border-slate-700 bg-slate-800 text-base text-white placeholder:text-slate-400 focus-visible:ring-brand-copper sm:h-14";
const selectClass = "mt-3 h-12 border-slate-700 bg-slate-800 text-base text-white focus:ring-brand-copper sm:h-14";

export const ConsultationForm = () => {
  const [form, setForm] = useState(initialForm);
  const [appointmentStart, setAppointmentStart] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!appointmentStart) {
      toast.error("Please choose an available consultation time");
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/consultations`, { ...form, appointment_start: appointmentStart });
      setConfirmation(response.data);
      toast.success("Consultation scheduled", {
        description: response.data.appointment_label,
      });
      setForm(initialForm);
      setAppointmentStart("");
      setRefreshSignal((current) => current + 1);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409) {
        toast.error(detail || "That appointment time was just booked");
        setAppointmentStart("");
        setRefreshSignal((current) => current + 1);
      } else {
        toast.error(detail || "Your request could not be submitted. Please call the office.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full min-w-0 rounded-lg bg-brand-navy p-5 text-white shadow-editorial sm:p-10 lg:p-12" data-testid="consultation-request-form">
      <p className="overline mb-4 text-brand-copper">Appointment Request</p>
      <h2 className="font-serif text-3xl font-bold sm:text-4xl">Schedule an Immigration Consultation</h2>
      <p className="mt-3 text-sm leading-7 text-white/68">
        Choose a real available time. Your request is stored securely and sent directly to Egaal’s office inbox.
      </p>
      <input
        type="text"
        value={form.website}
        onChange={(e) => update("website", e.target.value)}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        data-testid="consultation-honeypot-input"
      />
      <div className="mt-8 grid gap-6 sm:mt-10 sm:grid-cols-2 sm:gap-7">
        <div>
          <Label htmlFor="consultation-name" className="text-sm text-white/82">Full name</Label>
          <Input id="consultation-name" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="consultation-name-input" className={fieldClass} placeholder="Your full name" />
        </div>
        <div>
          <Label htmlFor="consultation-phone" className="text-sm text-white/82">Phone</Label>
          <Input id="consultation-phone" required type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="consultation-phone-input" className={fieldClass} placeholder="(206) 000-0000" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="consultation-email" className="text-sm text-white/82">Email</Label>
          <Input id="consultation-email" required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="consultation-email-input" className={fieldClass} placeholder="you@example.com" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-sm text-white/82">Service Needed</Label>
          <Select value={form.service} onValueChange={(value) => update("service", value)} required>
            <SelectTrigger data-testid="consultation-service-select" className={selectClass}>
              <SelectValue placeholder="Choose a service" />
            </SelectTrigger>
            <SelectContent className="max-h-96">
              {SERVICE_GROUPS.map((group) => (
                <SelectGroup key={group.label}>
                  <SelectLabel data-testid={`service-group-${group.label.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and")}`} className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-navy/55">{group.label}</SelectLabel>
                  {group.options.map(([value, label]) => (
                    <SelectItem key={value} value={value} data-testid={`service-option-${value}`}>{label}</SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm text-white/82">Current stage</Label>
          <Select value={form.stage} onValueChange={(value) => update("stage", value)} required>
            <SelectTrigger data-testid="consultation-stage-select" className={selectClass}>
              <SelectValue placeholder="Select your stage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="starting" data-testid="stage-option-starting">Just getting started</SelectItem>
              <SelectItem value="documents" data-testid="stage-option-documents">Gathering documents</SelectItem>
              <SelectItem value="filed" data-testid="stage-option-filed">Application already filed</SelectItem>
              <SelectItem value="notice" data-testid="stage-option-notice">Received a notice or interview</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0 sm:col-span-2">
          <AppointmentPicker value={appointmentStart} onChange={setAppointmentStart} refreshSignal={refreshSignal} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="consultation-message" className="text-sm text-white/82">How can we help?</Label>
          <Textarea id="consultation-message" required value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="consultation-message-textarea" className="mt-3 min-h-40 border-slate-700 bg-slate-800 text-base text-white placeholder:text-slate-400 focus-visible:ring-brand-copper" placeholder="Tell us about your immigration goals, notices, deadlines, or questions." />
        </div>
      </div>
      <Button type="submit" size="lg" disabled={submitting} data-testid="consultation-submit-button" className="mt-8 h-14 w-full rounded-full bg-brand-copper text-base text-brand-navy hover:bg-brand-copper/90 disabled:opacity-70">
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
        {submitting ? "Scheduling Consultation…" : "Request Consultation"}
      </Button>
      {confirmation && (
        <div className="mt-5 rounded-lg border border-brand-copper/30 bg-brand-copper/10 p-5" data-testid="consultation-confirmation-panel">
          <p className="text-sm font-extrabold text-white">Consultation scheduled for {confirmation.appointment_label}</p>
          <p className="mt-2 text-sm leading-6 text-white/62">A confirmation email includes your secure management link.</p>
          <Link to={confirmation.manage_path} data-testid="consultation-manage-booking-link" className="mt-4 inline-flex rounded-full border border-brand-copper px-5 py-3 text-sm font-extrabold text-brand-copper transition-colors hover:bg-brand-copper hover:text-brand-navy">
            Manage or Reschedule Appointment
          </Link>
        </div>
      )}
    </form>
  );
};
